import { useEffect, useState } from 'react';
import { AppRenderer } from './renderer/AppRenderer';
import type { AppConfig } from '@devfactory/core';

// API URL for the staging environment
const API_URL = import.meta.env.VITE_API_URL || 'https://devfactory-api-staging.yassine-techini.workers.dev';

export function RuntimeApp() {
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadAppConfig() {
      try {
        // Get app slug from URL
        const appSlug = getAppSlugFromUrl();

        if (!appSlug) {
          throw new Error('No application specified');
        }

        // Fetch published app by slug from the public API
        const response = await fetch(`${API_URL}/public/apps/${appSlug}`);

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(`Application "${appSlug}" not found`);
          }
          throw new Error('Failed to load app configuration');
        }

        const result = await response.json();

        if (!result.success || !result.data) {
          throw new Error('Invalid app data received');
        }

        // Transform app data to config format
        const app = result.data;
        const appConfig = app.config || {};
        setAppConfig({
          id: app.id,
          name: app.name,
          slug: app.slug,
          description: app.description,
          theme: appConfig.theme || {},
          pages: appConfig.pages || [],
          navigation: appConfig.navigation || [],
        } as AppConfig);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    }

    loadAppConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !appConfig) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Error</h1>
          <p className="mt-2 text-gray-600">{error || 'App not found'}</p>
        </div>
      </div>
    );
  }

  return <AppRenderer config={appConfig} />;
}

function getAppSlugFromUrl(): string | null {
  // Get from path: /apps/:slug
  const path = window.location.pathname;
  const match = path.match(/^\/apps\/([^/]+)/);

  if (match?.[1]) {
    return match[1];
  }

  // Try to get from subdomain
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  if (parts.length > 2 && parts[0] && !parts[0].includes('devfactory')) {
    return parts[0];
  }

  return null;
}
